/**
 * {@link PlayerController} — the headless player.
 *
 * Owns what no engine can: the munging pipeline, the state store and
 * coming-soon polling. Detection and recovery belong to the engine, which
 * reaches back through exactly one door — `reload-requested`, the rebuild of a
 * munged source that only this code knows how to perform. The engine is reached
 * through {@link PlayerAdapter}, so the same controller drives Video.js on the
 * web today and AVPlayer / ExoPlayer in a Capacitor shell later.
 */
import { Emitter } from './emitter.js';
import { isMissing, toPlayerError } from './pipeline/fetch.js';
import { DEFAULT_ANGLE_ID, dedupeByName, loadMaster, mungeSource, } from './pipeline/pipeline.js';
import { sortQualities, toQuality } from './pipeline/quality-cap.js';
import { Poller } from './poller.js';
import { readSubtitleCues } from './pipeline/subtitle-cues.js';
import { DEFAULT_LEAD_SECONDS, DEFAULT_WARM_BYTES, buildChunkSchedules, } from './prefetch.js';
import { SidecarLoader, pickDefaultChapterTrack } from './sidecars.js';
import { findThumbnailCue, thumbnailsCoverDuration, } from '@luminary-media-converter/hls-core';
import { StateStore, createInitialState } from './store.js';
import { SleepTimer } from './sleepTimer.js';
import { chapterIndexAt } from './chapters.js';
import { AUDIO_ONLY_ANGLE_ID, DATA_SAVER_START_BPS, resolveRecoveryPolicy, } from './types.js';
export class PlayerController {
    adapter;
    options;
    store = new StateStore();
    emitter = new Emitter();
    adapterSubscriptions = [];
    fetchImpl;
    subtle;
    serveStrategy;
    policy = resolveRecoveryPolicy();
    poller = null;
    sidecarLoader = null;
    /** Parsed scrub-preview cues for the current source; empty when there are none. */
    thumbnailCues = [];
    /** The sleep timer; a minutes timer outlives a source, so it is not torn down with one. */
    sleep = new SleepTimer(() => this.adapter.pause(), (sleepTimer) => this.store.setState({ sleepTimer }));
    /** Bumped by every load(); stale async work checks it and bails. */
    generation = 0;
    source = null;
    master = null;
    /** Each subtitle track's lines, read once per master. */
    subtitleCues = new Map();
    cache = new Map();
    /** What this source has served; see {@link ServeMemo}. */
    served = { playlists: new Map() };
    /**
     * The sidecar subtitles handed to the adapter for this source. An adapter
     * drops its text tracks with the source they belong to, on every
     * `loadSource`, so each attach after the first has to hand them back.
     */
    sidecarTextTracks = [];
    /**
     * The audio track somebody chose through {@link setAudioTrack} — a host's
     * selector, a viewer's pick relayed from the engine's own menu, a
     * preferred-language rule. Null until somebody chooses; cleared by
     * `load()`, since a new source is a new decision.
     *
     * Kept apart from `activeAudioTrackId` because it has to outlive the
     * engine's track list. Every attach after the first — an angle switch, the
     * audio toggle, a re-munge — hands the engine a new source, so does an
     * adapter's own `reattach()`, and each rebuilds the list with the stream's
     * default selected in it.
     */
    chosenAudioTrackId = null;
    /**
     * The choice has yet to reach the engine's current list: it was made while
     * the list did not carry it, or the list has been torn down since. Cleared
     * when a list carrying it arrives and it is handed back.
     */
    audioChoicePending = false;
    qualityToVariant = new Map();
    /**
     * A quality chosen through {@link setQuality} on an engine that cannot pin
     * a rendition, which the munge applies as a height cap on every attach —
     * an angle switch and a recovery re-munge keep it. Null for `'auto'`, and
     * cleared by `load()`, since a new source is a new decision.
     */
    qualityChoice = null;
    startPosition = 0;
    resumePlaying = false;
    chapterTrackPinned = false;
    constructor(adapter, options) {
        this.adapter = adapter;
        this.options = options;
        this.fetchImpl =
            options.fetchImpl ??
                ((input, init) => globalThis.fetch(input, init));
        this.subtle = options.subtle;
        this.serveStrategy = options.serveStrategy;
        this.attachAdapter();
    }
    // -----------------------------------------------------------------------
    // Loading
    // -----------------------------------------------------------------------
    async load(source) {
        if (this.state.lifecycle === 'destroyed')
            return;
        const generation = ++this.generation;
        const preserved = source.preservePosition
            ? this.adapter.getCurrentTime()
            : (source.startPosition ?? 0);
        const resume = source.preservePosition ? this.state.playing : false;
        this.teardownSource();
        this.sleep.onNewSource();
        this.source = source;
        this.policy = resolveRecoveryPolicy(source.recovery);
        this.startPosition = preserved;
        this.resumePlaying = resume;
        this.chapterTrackPinned = false;
        this.chosenAudioTrackId = null;
        this.audioChoicePending = false;
        this.qualityChoice = null;
        this.cache = new Map();
        // `teardownSource` above released every URL the last source was
        // served at, so nothing it memoized can be handed out again.
        this.served = { playlists: new Map() };
        this.sidecarTextTracks = [];
        this.qualityToVariant = new Map();
        // Cleared here, not only when the next set arrives: a load that fails
        // before its sidecars would otherwise leave the previous video's frames
        // available to `thumbnailAt`.
        this.thumbnailCues = [];
        this.store.reset({
            ...createInitialState(),
            lifecycle: 'loading',
            sleepTimer: this.sleep.state,
            playbackRate: this.state.playbackRate,
            maxHeight: source.maxHeight,
        });
        try {
            // Inside the try, not above it: a serving layer may throw on first
            // use — a native shell whose loopback server failed to bind, say.
            // Every host calls `load()` fire-and-forget, because the contract is
            // that failures surface through state; a throw here would escape as
            // an unhandled rejection instead of rendering the error panel.
            this.sidecarLoader = new SidecarLoader({
                fetchImpl: this.fetchImpl,
                serveStrategy: this.serveStrategy,
                keyHex: source.keyHex,
                subtle: this.subtle,
            });
            const info = await loadMaster(source.masterUrl, this.context());
            if (generation !== this.generation)
                return;
            await this.applyMaster(generation, info);
        }
        catch (error) {
            if (generation !== this.generation)
                return;
            if (isMissing(error) && (source.poll?.enabled ?? true)) {
                // The engine may still hold the PREVIOUS source (teardown
                // stops our plumbing, not the adapter). "Coming soon" over
                // silently continuing stale playback is a lie — stop it.
                this.adapter.pause();
                this.store.setState({
                    lifecycle: 'waiting-for-master',
                    playing: false,
                });
                this.startPolling(generation, source);
                return;
            }
            this.fail(toPlayerError(error));
        }
    }
    /**
     * Wait for a master that is not published yet. The first check already
     * happened in `load()`, so the poller only runs the fixed-interval ones.
     * Finding it emits `master-available` and continues the load — it never
     * autoplays; the implementing project decides that.
     */
    startPolling(generation, source) {
        let found = null;
        this.poller = new Poller({
            intervalMs: source.poll?.intervalMs,
            immediate: false,
            check: async () => {
                if (generation !== this.generation)
                    return false;
                try {
                    found = await loadMaster(source.masterUrl, this.context());
                    return true;
                }
                catch (error) {
                    if (isMissing(error))
                        return false;
                    throw error;
                }
            },
            onAvailable: () => {
                if (generation !== this.generation || !found)
                    return;
                this.emitter.emit('master-available', undefined);
                void this.applyMaster(generation, found).catch((error) => {
                    if (generation === this.generation) {
                        this.fail(toPlayerError(error));
                    }
                });
            },
            onError: (error) => {
                if (generation === this.generation) {
                    this.fail(toPlayerError(error));
                }
            },
        });
        this.poller.start();
    }
    async applyMaster(generation, info) {
        this.master = info;
        this.subtitleCues.clear();
        const requested = this.source?.startAngleId;
        const angleId = requested && info.angles.some((angle) => angle.id === requested)
            ? requested
            : defaultAngleId(info.angles);
        this.store.setState({
            lifecycle: 'loading',
            bufferedEnd: 0,
            angles: info.angles,
            activeAngleId: angleId,
            audioTracks: info.audioTracks,
            activeAudioTrackId: info.audioTracks[0]?.id ?? null,
            subtitleTracks: info.subtitleTracks,
            // A load starts with subtitles off on every engine; one left marked from the last video reads as on, and
            // a host waiting for "off" before applying the viewer's choice would never apply it.
            activeSubtitleTrackId: null,
            isAudioOnly: info.nativelyAudioOnly,
            chapterTracks: this.prepareChapterTracks(),
        });
        await this.attachAngle(generation, angleId, {
            seekTo: this.startPosition,
            resume: this.resumePlaying,
        });
        if (generation !== this.generation)
            return;
        await this.attachSidecars(generation, info);
    }
    /** Steps 3–6 of the pipeline, plus engine attach. Used by load/setAngle/reload. */
    async attachAngle(generation, angleId, options) {
        const info = this.master;
        if (!info)
            return;
        const source = this.adapter.capabilities.nativeHls
            ? {
                url: info.url,
                isBlob: false,
                ...(this.source?.keyHex
                    ? { keyHex: this.source.keyHex }
                    : {}),
            }
            : null;
        const munged = source
            ? null
            : await mungeSource(info, {
                angleId,
                maxHeight: this.source?.maxHeight,
                selectHeight: this.qualityChoice?.height,
            }, this.context());
        if (generation !== this.generation)
            return;
        // The policy is completed here rather than in the pipeline: it is the
        // controller that resolves a source's overrides, and the adapter that
        // runs the ladder on the result.
        const dataSaver = this.source?.dataSaver === true;
        const measured = this.source?.bandwidthEstimate;
        const usable = measured && measured > 0 && Number.isFinite(measured) ? measured : undefined;
        // A data saver starts low whatever the host measured: the estimate is the one input both
        // engines already start from, so clamping it here needs no engine to know about data savers.
        const bandwidth = dataSaver ? Math.min(usable ?? Infinity, DATA_SAVER_START_BPS) : usable;
        await this.adapter.loadSource({
            ...(source ?? munged.source),
            ...(bandwidth !== undefined ? { bandwidthEstimate: bandwidth } : {}),
            ...(dataSaver ? { dataSaver } : {}),
            recovery: this.policy,
        });
        if (generation !== this.generation)
            return;
        // The adapter dropped the sidecar subtitles with the source it just
        // replaced. Empty on a first attach: they are loaded after it.
        if (this.sidecarTextTracks.length > 0) {
            this.adapter.setTextTracks(this.sidecarTextTracks);
        }
        // The narrowed master references only this angle's renditions plus the
        // audio group, so the schedules built from it are exactly the chains
        // playback is about to pull — an angle switch comes back through here
        // and rebuilds them.
        this.updateChunkWarming(munged);
        this.store.setState({
            lifecycle: 'ready',
            activeAngleId: angleId,
            activeQualityId: this.qualityChoice?.id ?? 'auto',
            qualities: munged?.qualities ?? [],
            isAudioOnly: munged?.isAudioOnly ??
                (info.nativelyAudioOnly || angleId === AUDIO_ONLY_ANGLE_ID),
            live: munged?.isLive ?? false,
            error: null,
        });
        this.refreshQualities();
        this.refreshAudioTracks();
        if (options.seekTo > 0)
            this.adapter.seek(options.seekTo);
        if (options.resume) {
            await this.adapter.play().catch(() => undefined);
        }
    }
    async attachSidecars(generation, info) {
        const loader = this.sidecarLoader;
        if (!loader)
            return;
        // An engine that cannot render side-loaded text gets none: the files
        // are not fetched, and the tracks are not offered. Subtitles the master
        // carries are the engine's own, and stay.
        if (this.adapter.capabilities.renderText) {
            try {
                const { tracks, adapterTracks } = await loader.loadSubtitleTracks(this.source?.sidecars?.subtitles);
                if (generation !== this.generation)
                    return;
                if (adapterTracks.length > 0) {
                    this.sidecarTextTracks = adapterTracks;
                    this.adapter.setTextTracks(adapterTracks);
                    this.store.setState({
                        subtitleTracks: [...info.subtitleTracks, ...tracks],
                    });
                }
            }
            catch (error) {
                if (generation !== this.generation)
                    return;
                // Subtitles are an enhancement — report, but keep playing.
                this.emitNonFatal(toPlayerError(error));
            }
        }
        if (generation !== this.generation)
            return;
        this.selectDefaultChapterTrack(generation);
        if (generation !== this.generation)
            return;
        await this.attachThumbnails(generation);
    }
    /**
     * Fetch and parse the scrub-preview sidecar, if there is one.
     *
     * Deliberately last of the sidecars and deliberately silent: a preview is
     * the least of what a viewer came for, and every way it can be absent — no
     * sidecar, an audio-only encode, `thumbnails: false`, a 404 — is normal
     * rather than an error worth surfacing. `loadThumbnails` already collapses
     * those to an empty list.
     */
    async attachThumbnails(generation) {
        const cues = (await this.sidecarLoader?.loadThumbnails(this.source?.sidecars?.thumbnails)) ?? [];
        if (generation !== this.generation)
            return;
        this.thumbnailCues = cues;
        this.updateThumbnailsReady();
    }
    /** Ready once there are cues that span the video; re-asked when the duration becomes known. */
    updateThumbnailsReady() {
        this.store.setState({
            thumbnailsReady: thumbnailsCoverDuration(this.thumbnailCues, this.state.duration),
        });
    }
    /**
     * The sprite frame to show for `timeSec`, or null when there is none.
     *
     * A lookup rather than state, because a scrub asks per pointer move and the
     * answer is not something the rest of the UI reacts to — putting it in the
     * store would publish a change to every subscriber on every mouse move.
     */
    thumbnailAt(timeSec) {
        if (this.thumbnailCues.length === 0)
            return null;
        const cue = findThumbnailCue(this.thumbnailCues, timeSec);
        if (!cue)
            return null;
        // A frame is sampled at its cue's start, so the second half of a cue is nearer the next frame than its own.
        const span = cue.endTime - cue.startTime;
        const next = span > 0 ? findThumbnailCue(this.thumbnailCues, cue.endTime) : undefined;
        return next && timeSec - cue.startTime > span / 2 ? next : cue;
    }
    // -----------------------------------------------------------------------
    // Playback controls
    // -----------------------------------------------------------------------
    async play() {
        await this.adapter.play();
    }
    pause() {
        this.adapter.pause();
    }
    togglePlay() {
        if (this.state.playing)
            this.adapter.pause();
        else
            void this.adapter.play().catch(() => undefined);
    }
    seek(seconds) {
        this.adapter.seek(seconds);
        this.store.setState({ currentTime: seconds });
    }
    setSleepTimer(option) {
        if (this.state.lifecycle === 'destroyed')
            return;
        let chapterEnd;
        if (option?.kind === 'chapter') {
            const { chapters, currentTime, duration } = this.state;
            const index = chapterIndexAt(chapters, currentTime);
            if (index < 0)
                return;
            chapterEnd = chapters[index + 1]?.startTime ?? (Number.isFinite(duration) && duration > 0 ? duration : undefined);
        }
        this.sleep.set(option, chapterEnd);
        // A shell may keep the minutes timer beside the engine, where a suspended web view cannot stop it.
        this.adapter.setSleepTimer?.(this.sleep.state?.kind === 'minutes' ? this.sleep.state.endsAt : null);
    }
    setPlaybackRate(rate) {
        this.adapter.setPlaybackRate(rate);
        this.store.setState({ playbackRate: rate });
    }
    async setAngle(id) {
        if (!this.master || this.state.lifecycle === 'destroyed')
            return;
        if (id === this.state.activeAngleId)
            return;
        if (!this.state.angles.some((angle) => angle.id === id))
            return;
        const generation = this.generation;
        const seekTo = this.adapter.getCurrentTime();
        const resume = this.state.playing;
        try {
            await this.attachAngle(generation, id, { seekTo, resume });
        }
        catch (error) {
            if (generation !== this.generation)
                return;
            this.fail(toPlayerError(error));
            return;
        }
        if (generation !== this.generation)
            return;
        this.emitter.emit('angle-changed', { angleId: id });
    }
    setQuality(id) {
        if (!this.adapter.capabilities.variantSwitching) {
            void this.capQuality(id);
            return;
        }
        const variantId = id === 'auto' ? 'auto' : (this.qualityToVariant.get(id) ?? id);
        this.adapter.setVariant(variantId);
        this.store.setState({ activeQualityId: id });
    }
    /**
     * A quality choice for an engine that cannot pin a rendition (AVPlayer):
     * re-munge the current angle with the chosen height as a cap, keeping
     * position and play state, so the engine's own ABR stays at or below it.
     * `'auto'` lifts the cap back to the source's own `maxHeight`.
     *
     * A resolution-less quality has no height to cap at, so choosing one does
     * nothing.
     */
    async capQuality(id) {
        if (!this.master || this.state.lifecycle === 'destroyed')
            return;
        let choice = null;
        if (id !== 'auto') {
            const height = this.state.qualities.find((q) => q.id === id)?.height;
            if (height === undefined)
                return;
            choice = { id, height };
        }
        if (choice?.height === this.qualityChoice?.height)
            return;
        this.qualityChoice = choice;
        this.store.setState({ activeQualityId: id });
        const generation = this.generation;
        const seekTo = this.adapter.getCurrentTime();
        const resume = this.state.playing;
        try {
            await this.attachAngle(generation, this.state.activeAngleId, {
                seekTo,
                resume,
            });
        }
        catch (error) {
            if (generation !== this.generation)
                return;
            this.fail(toPlayerError(error));
        }
    }
    setAudioTrack(id) {
        this.chosenAudioTrackId = id;
        // A list that does not carry it yet — the engine's has not arrived, or
        // is being rebuilt — gets it handed over once it does.
        this.audioChoicePending = !this.adapter
            .getAudioTracks()
            .some((track) => track.id === id);
        this.adapter.setAudioTrack(id);
        this.store.setState({ activeAudioTrackId: id });
        if (!this.chapterTrackPinned) {
            this.selectDefaultChapterTrack(this.generation);
        }
    }
    setSubtitleTrack(id) {
        const track = id === null ? undefined : this.state.subtitleTracks.find((t) => t.id === id);
        this.adapter.setActiveTextTrack(id, track ? { label: track.label, ...(track.lang ? { lang: track.lang } : {}) } : undefined);
        this.store.setState({ activeSubtitleTrackId: id });
    }
    readSubtitleCues(id) {
        const master = this.master;
        if (!master)
            return Promise.resolve([]);
        const cached = this.subtitleCues.get(id);
        if (cached)
            return cached;
        const read = readSubtitleCues(master, id, {
            fetchImpl: this.fetchImpl,
            keyHex: this.source?.keyHex,
            subtle: this.subtle,
        }).catch(() => {
            // A failed read is tried again the next time it is asked for.
            if (this.subtitleCues.get(id) === read)
                this.subtitleCues.delete(id);
            return [];
        });
        this.subtitleCues.set(id, read);
        return read;
    }
    setChapterTrack(id) {
        this.chapterTrackPinned = true;
        void this.applyChapterTrack(this.generation, id);
    }
    // -----------------------------------------------------------------------
    // Store / events
    // -----------------------------------------------------------------------
    getState() {
        return this.store.getState();
    }
    subscribe(listener) {
        return this.store.subscribe(listener);
    }
    on(event, listener) {
        return this.emitter.on(event, listener);
    }
    destroy() {
        if (this.state.lifecycle === 'destroyed')
            return;
        this.generation += 1;
        this.teardownSource();
        this.sleep.clear();
        for (const unsubscribe of this.adapterSubscriptions)
            unsubscribe();
        this.adapterSubscriptions.length = 0;
        this.adapter.destroy();
        this.store.setState({ lifecycle: 'destroyed', playing: false });
        this.emitter.emit('destroyed', undefined);
        this.emitter.clear();
        this.store.clearListeners();
    }
    // -----------------------------------------------------------------------
    // Internals
    // -----------------------------------------------------------------------
    get state() {
        return this.store.getState();
    }
    context() {
        return {
            fetchImpl: this.fetchImpl,
            serveStrategy: this.serveStrategy,
            keyDelivery: this.adapter.capabilities.keyDelivery,
            keyHex: this.source?.keyHex,
            subtle: this.subtle,
            cache: this.cache,
            served: this.served,
        };
    }
    /**
     * Hand the adapter the chunk chains the munge just attached will pull,
     * replacing whatever it was warming before. A native-HLS load has no munged
     * playlists to read, a source without byte ranges yields no schedules, and
     * warming can be switched off outright — all three arrive as an empty
     * array, which the contract defines as "stop", so the call is unconditional
     * and there is one path for both arming and stopping.
     *
     * Every tuning value is resolved here: the adapter is handed decisions, not
     * a partially filled options bag it would have to know the defaults for.
     */
    updateChunkWarming(munged) {
        const settings = this.options.prefetch;
        const options = {
            leadSeconds: settings?.leadSeconds ?? DEFAULT_LEAD_SECONDS,
            warmBytes: settings?.warmBytes ?? DEFAULT_WARM_BYTES,
            fetchImpl: this.fetchImpl,
            ...(settings?.debug
                ? {
                    log: (message) => console.info(`[luminary-prefetch] ${message}`),
                }
                : {}),
        };
        const schedules = settings?.enabled === false || !munged
            ? []
            : buildChunkSchedules(munged.mediaPlaylists);
        this.adapter.warmChunks?.(schedules, options);
    }
    /** Release the previous generation's resources. */
    teardownSource() {
        this.poller?.stop();
        this.poller = null;
        this.updateChunkWarming(null);
        this.serveStrategy.release();
        this.master = null;
        this.subtitleCues.clear();
    }
    prepareChapterTracks() {
        this.sidecarLoader?.setChapters(this.source?.sidecars?.chapters);
        return this.sidecarLoader?.chapterTracks() ?? [];
    }
    selectDefaultChapterTrack(generation) {
        const tracks = this.state.chapterTracks;
        if (tracks.length === 0)
            return;
        const activeAudioLang = this.state.audioTracks.find((track) => track.id === this.state.activeAudioTrackId)?.lang;
        const id = pickDefaultChapterTrack(tracks, activeAudioLang);
        if (!id || id === this.state.activeChapterTrackId)
            return;
        void this.applyChapterTrack(generation, id);
    }
    async applyChapterTrack(generation, id) {
        if (!id) {
            this.store.setState({ activeChapterTrackId: null, chapters: [] });
            return;
        }
        this.store.setState({ activeChapterTrackId: id });
        try {
            const chapters = (await this.sidecarLoader?.loadChapters(id)) ?? [];
            if (!this.isCurrentChapterTrack(generation, id))
                return;
            this.store.setState({ chapters });
        }
        catch (error) {
            if (!this.isCurrentChapterTrack(generation, id))
                return;
            this.store.setState({ chapters: [] });
            this.emitNonFatal(toPlayerError(error));
        }
    }
    /**
     * A language's cues arrive asynchronously; by then the user may have picked
     * another track (or another source). Only the selection still on screen
     * wins.
     */
    isCurrentChapterTrack(generation, id) {
        return (generation === this.generation &&
            this.state.activeChapterTrackId === id);
    }
    /**
     * Qualities the ENGINE actually offers win over the playlist-derived ones —
     * same id scheme either way, so `setQuality()` keeps working while the
     * variant ids stay adapter-private.
     */
    refreshQualities() {
        const variants = this.adapter.getVariants();
        if (variants.length === 0)
            return;
        const map = new Map();
        const qualities = [];
        for (const variant of variants) {
            // The engine reports no width, so the playlist's own is kept.
            const known = this.state.qualities.find((q) => q.height === variant.height);
            const quality = toQuality(variant.height, variant.bandwidth, known?.width);
            if (!map.has(quality.id))
                qualities.push(quality);
            map.set(quality.id, variant.id);
        }
        this.qualityToVariant = map;
        const sorted = sortQualities(qualities);
        if (sameIds(sorted, this.state.qualities))
            return;
        this.store.setState({ qualities: sorted });
    }
    refreshAudioTracks() {
        const tracks = this.adapter.getAudioTracks();
        if (tracks.length === 0) {
            // The engine tore its list down, and the one it builds next has
            // its own default selected. See `PlayerAdapter.getAudioTracks`.
            if (this.chosenAudioTrackId !== null)
                this.audioChoicePending = true;
            return;
        }
        const mapped = dedupeByName(tracks.map((track) => ({
            id: track.id,
            lang: track.lang,
            label: track.label,
        })));
        const chosen = this.chosenAudioTrackId;
        const chosenListed = chosen !== null && mapped.some((track) => track.id === chosen);
        if (this.audioChoicePending && chosenListed) {
            this.audioChoicePending = false;
            this.handBackAudioChoice(chosen);
        }
        if (sameIds(mapped, this.state.audioTracks))
            return;
        const active = chosenListed
            ? chosen
            : mapped.some((track) => track.id === this.state.activeAudioTrackId)
                ? this.state.activeAudioTrackId
                : (mapped[0]?.id ?? null);
        this.store.setState({
            audioTracks: mapped,
            activeAudioTrackId: active,
        });
    }
    /**
     * Selects the chosen track again in a list the engine has just rebuilt.
     *
     * A microtask late rather than now, because this runs while the engine is
     * still announcing its tracks: video.js announces each one before it starts
     * listening to it, so a track enabled from inside the announcement is
     * enabled without the list — or VHS — ever hearing of it, and the default
     * plays on with both marked enabled. By the next microtask the list is
     * whole and listening.
     */
    handBackAudioChoice(id) {
        const generation = this.generation;
        void Promise.resolve().then(() => {
            if (generation !== this.generation)
                return;
            // A newer choice selected itself. A list torn down again in the
            // meantime is pending again, and its rebuild hands this back.
            if (this.chosenAudioTrackId !== id)
                return;
            if (!this.adapter.getAudioTracks().some((track) => track.id === id)) {
                return;
            }
            this.adapter.setAudioTrack(id);
        });
    }
    attachAdapter() {
        this.adapterSubscriptions.push(this.adapter.on('timeupdate', ({ currentTime }) => {
            this.store.setState({ currentTime });
            this.sleep.onTime(currentTime);
        }), this.adapter.on('durationchange', ({ duration }) => {
            this.store.setState({ duration });
            this.updateThumbnailsReady();
        }), this.adapter.on('progress', ({ bufferedEnd }) => {
            this.store.setState({ bufferedEnd });
        }), this.adapter.on('playing', () => {
            // Playing is proof the engine is working: after an error, it got there by itself (its
            // own recovery, a retry), and an error panel left over it would misstate that.
            const recovered = this.state.lifecycle === 'error';
            this.store.setState({
                playing: true,
                ended: false,
                stalled: false,
                ...(recovered ? { lifecycle: 'ready', error: null } : {}),
            });
        }), this.adapter.on('pause', () => {
            this.store.setState({ playing: false, stalled: false });
        }), this.adapter.on('ended', () => {
            this.store.setState({
                playing: false,
                ended: true,
                stalled: false,
            });
        }), 
        // 'waiting' is normal buffering. Whether it has gone on long enough
        // to count as a stall is the engine's call, made against its own
        // buffer and delivered as 'stalled'; the wrapper only publishes it.
        this.adapter.on('stalled', ({ stalled }) => {
            this.store.setState({ stalled });
        }), this.adapter.on('seeked', () => {
            this.store.setState({
                currentTime: this.adapter.getCurrentTime(),
            });
        }), 
        // By contract an adapter has already spent its recovery obligation
        // before raising a fatal error — its engine's own primitive, bounded
        // re-attaches, and any re-munge it asked for below. There is nothing
        // left for the wrapper to try, so this is the end of the road.
        this.adapter.on('error', (payload) => {
            if (!payload.fatal)
                return;
            this.fail(adapterErrorToPlayerError(payload));
        }), 
        // The one recovery step an engine cannot perform for itself.
        this.adapter.on('reload-requested', ({ attempt }) => {
            void this.remunge(attempt);
        }), this.adapter.on('variants-updated', () => this.refreshQualities()), this.adapter.on('audiotracks-updated', () => this.refreshAudioTracks()));
    }
    /**
     * Rebuild and re-attach the current selection, at an adapter's request.
     *
     * The same angle and the same cap: this is not a change of source but a
     * rebuild of it, for the failures re-attaching could not fix. A throw here
     * is terminal — the adapter asked for the last thing anyone could do.
     */
    async remunge(attempt) {
        if (this.state.lifecycle === 'destroyed')
            return;
        const generation = this.generation;
        const seekTo = this.adapter.getCurrentTime();
        const resume = this.state.playing;
        try {
            await this.attachAngle(generation, this.state.activeAngleId, {
                seekTo,
                resume,
            });
        }
        catch (error) {
            if (generation !== this.generation)
                return;
            this.fail(toPlayerError(error));
            return;
        }
        if (generation !== this.generation)
            return;
        this.emitter.emit('recovered', { attempt });
    }
    fail(error) {
        // Same reasoning as the waiting-for-master path: the adapter may
        // still be playing a previous source; an error surface over live
        // playback misstates both.
        this.adapter.pause();
        this.store.setState({ lifecycle: 'error', error, playing: false });
        this.emitter.emit('error', error);
    }
    emitNonFatal(error) {
        this.emitter.emit('error', { ...error, fatal: false });
    }
}
/**
 * An exhausted adapter's error, as something a viewer can be shown. The
 * category is the engine's account of what went wrong; the message says the
 * recovery everyone agreed to try has been tried.
 */
function adapterErrorToPlayerError(payload) {
    const code = payload.category === 'network'
        ? 'network'
        : payload.category === 'media'
            ? 'media'
            : 'unknown';
    return {
        code,
        fatal: true,
        message: `Playback failed: unrecoverable ${payload.category} error`,
        cause: payload.detail,
    };
}
function defaultAngleId(angles) {
    if (angles.length === 0)
        return null;
    const preferred = angles.find((angle) => angle.isDefault) ??
        angles.find((angle) => angle.id !== AUDIO_ONLY_ANGLE_ID) ??
        angles[0];
    return preferred?.id ?? DEFAULT_ANGLE_ID;
}
function sameIds(a, b) {
    if (a.length !== b.length)
        return false;
    return a.every((item, index) => item.id === b[index]?.id);
}
//# sourceMappingURL=controller.js.map